import { Injectable, OnDestroy } from '@angular/core';
import {
  EditSessionEvent,
  EditSessionMetadata,
  EditSessionRecord,
  EditSessionTimingConfig,
} from '../_models/edit-session.model';

@Injectable({ providedIn: 'root' })
export class EditSessionService implements OnDestroy {
  private static readonly CHANNEL_NAME = 'notesapp-edit-sessions';
  private static readonly TAB_ID_STORAGE_KEY = 'notesapp-edit-session-tab-id';
  private static readonly FALLBACK_EVENT_KEY = 'notesapp-edit-session-event';

  private readonly timing: EditSessionTimingConfig = {
    heartbeatIntervalMs: 12000,
    staleTimeoutMs: 45000,
    syncWaitMs: 150,
  };

  private readonly tabId: string;
  private readonly ownSessionsByNote = new Map<string, EditSessionRecord>();
  private readonly knownSessionsByCompositeKey = new Map<string, EditSessionRecord>();
  private readonly heartbeatTimersByNote = new Map<string, ReturnType<typeof setInterval>>();
  private stalePruneTimer: ReturnType<typeof setInterval> | null = null;

  private channel: BroadcastChannel | null = null;
  private storageListener: ((event: StorageEvent) => void) | null = null;
  private beforeUnloadListener: (() => void) | null = null;

  constructor() {
    this.tabId = this.getOrCreateTabId();
    this.initializeMessagingTransport();
    this.startStalePruneLoop();
    this.requestSyncFromOtherTabs();
  }

  ngOnDestroy(): void {
    this.unregisterAllOwnSessions();
    this.disposeTransport();
    if (this.stalePruneTimer) {
      clearInterval(this.stalePruneTimer);
      this.stalePruneTimer = null;
    }
  }

  /** This tab's stable instance id (persisted in sessionStorage), for autosave Stage 2 snapshots. */
  getTabId(): string {
    return this.tabId;
  }

  /** This tab's own active session record for a note, if one is registered. */
  getSessionRecord(noteId: string): EditSessionRecord | undefined {
    return this.ownSessionsByNote.get((noteId || '').trim());
  }

  getOrCreateTabId(): string {
    let tabId = '';
    try {
      tabId = sessionStorage.getItem(EditSessionService.TAB_ID_STORAGE_KEY) || '';
      if (!tabId) {
        tabId = this.generateId('tab');
        sessionStorage.setItem(EditSessionService.TAB_ID_STORAGE_KEY, tabId);
      }
    } catch {
      tabId = this.generateId('tab');
    }
    return tabId;
  }

  async checkForConflictingSession(noteId: string): Promise<EditSessionRecord | null> {
    const normalizedNoteId = (noteId || '').trim();
    if (!normalizedNoteId) {
      return null;
    }

    this.pruneStaleSessions();
    this.requestSyncFromOtherTabs();
    await this.wait(this.timing.syncWaitMs);
    this.pruneStaleSessions();

    for (const session of this.knownSessionsByCompositeKey.values()) {
      if (session.noteId !== normalizedNoteId) {
        continue;
      }
      if (session.tabId === this.tabId) {
        continue;
      }
      if (this.isSessionStale(session)) {
        continue;
      }
      return session;
    }

    return null;
  }

  registerEditSession(noteId: string, metadata?: EditSessionMetadata): void {
    const normalizedNoteId = (noteId || '').trim();
    if (!normalizedNoteId) {
      return;
    }

    const existing = this.ownSessionsByNote.get(normalizedNoteId);
    const nowIso = new Date().toISOString();
    const record: EditSessionRecord = existing
      ? {
          ...existing,
          route: metadata?.route ?? existing.route,
          title: metadata?.title ?? existing.title,
          lastHeartbeatAt: nowIso,
        }
      : {
          noteId: normalizedNoteId,
          tabId: this.tabId,
          sessionId: this.generateId('sess'),
          openedAt: nowIso,
          lastHeartbeatAt: nowIso,
          route: metadata?.route,
          title: metadata?.title,
        };

    this.ownSessionsByNote.set(normalizedNoteId, record);
    this.knownSessionsByCompositeKey.set(this.compositeKey(record), record);
    this.publish({ type: 'register', tabId: this.tabId, sentAt: nowIso, record });
    this.startHeartbeat(normalizedNoteId);
  }

  unregisterEditSession(noteId: string): void {
    const normalizedNoteId = (noteId || '').trim();
    if (!normalizedNoteId) {
      return;
    }

    this.stopHeartbeat(normalizedNoteId);

    const record = this.ownSessionsByNote.get(normalizedNoteId);
    if (!record) {
      return;
    }

    this.ownSessionsByNote.delete(normalizedNoteId);
    this.knownSessionsByCompositeKey.delete(this.compositeKey(record));
    this.publish({
      type: 'unregister',
      tabId: this.tabId,
      sentAt: new Date().toISOString(),
      record,
    });
  }

  startHeartbeat(noteId: string): void {
    const normalizedNoteId = (noteId || '').trim();
    if (!normalizedNoteId || this.heartbeatTimersByNote.has(normalizedNoteId)) {
      return;
    }

    const timer = setInterval(() => {
      const record = this.ownSessionsByNote.get(normalizedNoteId);
      if (!record) {
        this.stopHeartbeat(normalizedNoteId);
        return;
      }

      const updated: EditSessionRecord = {
        ...record,
        lastHeartbeatAt: new Date().toISOString(),
      };
      this.ownSessionsByNote.set(normalizedNoteId, updated);
      this.knownSessionsByCompositeKey.set(this.compositeKey(updated), updated);
      this.publish({
        type: 'heartbeat',
        tabId: this.tabId,
        sentAt: updated.lastHeartbeatAt,
        record: updated,
      });
    }, this.timing.heartbeatIntervalMs);

    this.heartbeatTimersByNote.set(normalizedNoteId, timer);
  }

  stopHeartbeat(noteId: string): void {
    const normalizedNoteId = (noteId || '').trim();
    const timer = this.heartbeatTimersByNote.get(normalizedNoteId);
    if (!timer) {
      return;
    }
    clearInterval(timer);
    this.heartbeatTimersByNote.delete(normalizedNoteId);
  }

  isSessionStale(session: EditSessionRecord): boolean {
    const lastHeartbeatMs = Date.parse(session.lastHeartbeatAt);
    if (!Number.isFinite(lastHeartbeatMs)) {
      return true;
    }
    return Date.now() - lastHeartbeatMs > this.timing.staleTimeoutMs;
  }

  private initializeMessagingTransport(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if ('BroadcastChannel' in window) {
      this.channel = new BroadcastChannel(EditSessionService.CHANNEL_NAME);
      this.channel.onmessage = (event: MessageEvent<EditSessionEvent>) => {
        this.handleIncomingEvent(event.data);
      };
    }

    this.storageListener = (event: StorageEvent) => {
      if (event.key !== EditSessionService.FALLBACK_EVENT_KEY || !event.newValue) {
        return;
      }
      try {
        const parsed = JSON.parse(event.newValue) as EditSessionEvent;
        this.handleIncomingEvent(parsed);
      } catch {
        // ignore malformed payload
      }
    };
    window.addEventListener('storage', this.storageListener);

    this.beforeUnloadListener = () => {
      this.unregisterAllOwnSessions();
    };
    window.addEventListener('beforeunload', this.beforeUnloadListener);
    window.addEventListener('pagehide', this.beforeUnloadListener);
  }

  private disposeTransport(): void {
    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    if (typeof window === 'undefined') {
      return;
    }

    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener);
      this.storageListener = null;
    }

    if (this.beforeUnloadListener) {
      window.removeEventListener('beforeunload', this.beforeUnloadListener);
      window.removeEventListener('pagehide', this.beforeUnloadListener);
      this.beforeUnloadListener = null;
    }
  }

  private requestSyncFromOtherTabs(): void {
    this.publish({
      type: 'sync-request',
      tabId: this.tabId,
      sentAt: new Date().toISOString(),
    });
  }

  private handleIncomingEvent(event: EditSessionEvent): void {
    if (!event || !event.type || event.tabId === this.tabId) {
      return;
    }

    switch (event.type) {
      case 'register':
      case 'heartbeat':
        if (event.record) {
          this.knownSessionsByCompositeKey.set(this.compositeKey(event.record), event.record);
          this.pruneStaleSessions();
        }
        break;
      case 'unregister':
        if (event.record) {
          this.knownSessionsByCompositeKey.delete(this.compositeKey(event.record));
        }
        break;
      case 'sync-request':
        this.respondWithOwnSessions();
        break;
      case 'sync-response':
        if (event.records?.length) {
          for (const record of event.records) {
            this.knownSessionsByCompositeKey.set(this.compositeKey(record), record);
          }
          this.pruneStaleSessions();
        }
        break;
      default:
        break;
    }
  }

  private respondWithOwnSessions(): void {
    const records = Array.from(this.ownSessionsByNote.values());
    this.publish({
      type: 'sync-response',
      tabId: this.tabId,
      sentAt: new Date().toISOString(),
      records,
    });
  }

  private publish(event: EditSessionEvent): void {
    if (this.channel) {
      this.channel.postMessage(event);
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    try {
      localStorage.setItem(EditSessionService.FALLBACK_EVENT_KEY, JSON.stringify(event));
      localStorage.removeItem(EditSessionService.FALLBACK_EVENT_KEY);
    } catch {
      // ignore quota/security failures in fallback transport
    }
  }

  private pruneStaleSessions(): void {
    for (const [key, session] of this.knownSessionsByCompositeKey.entries()) {
      if (this.isSessionStale(session)) {
        this.knownSessionsByCompositeKey.delete(key);
      }
    }
  }

  private startStalePruneLoop(): void {
    this.stalePruneTimer = setInterval(() => {
      this.pruneStaleSessions();
    }, this.timing.heartbeatIntervalMs);
  }

  private unregisterAllOwnSessions(): void {
    const noteIds = Array.from(this.ownSessionsByNote.keys());
    for (const noteId of noteIds) {
      this.unregisterEditSession(noteId);
    }
  }

  private compositeKey(session: EditSessionRecord): string {
    return `${session.noteId}::${session.tabId}::${session.sessionId}`;
  }

  private generateId(prefix: string): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return `${prefix}-${crypto.randomUUID()}`;
    }
    return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private wait(ms: number): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => resolve(), ms);
    });
  }
}
